import { isMockServices } from 'src/utils';
import mock from 'src/services/utils/mock';
import { MutationFunction } from '@tanstack/react-query';
import { StackFilterDocument } from './types';
import { FieldColumn } from '../fields/types';
import { pendingStackFilterId } from 'src/queries/documents/useCloneStackFilterMutation';
import { getCompatibleAuthTokenHttp } from '../utils/enhancedAuthTokenHttp';

export interface ColumnFilter {
  id: string;
  value: string;
}

export interface SetStackFilterParams {
  stackFilterId?: string;
  documentTypes?: StackFilterDocument[];
  fieldColumns?: FieldColumn[];
  columnFilters?: ColumnFilter[];
  isOrganization?: boolean;
  name: string;
  visibility?: 'public' | 'private';
}

const setStackFilter: MutationFunction<
  SetStackFilterParams & { id?: string },
  SetStackFilterParams
> = async (args) => {
  if (isMockServices())
    return await mock(_mockStackFilter(args), { delay: 4000, failureRate: 1 });

  // Determine visibility - if explicitly set, use that; otherwise derive from isOrganization
  const visibility =
    args.visibility ?? (args.isOrganization ? 'public' : 'private');

  // Prepare configuration based on the type of data provided
  const configuration: {
    columnFilters?: ColumnFilter[];
    orderedDocumentTypeIds?:
      | StackFilterDocument[]
      | { documentTypeId: string; required: boolean }[];
  } = {};

  // Add columnFilters if provided (including empty arrays to clear filters)
  if (args.columnFilters !== undefined) {
    configuration.columnFilters = args.columnFilters;
  }

  // Always set orderedDocumentTypeIds
  if (args.fieldColumns && args.fieldColumns.length > 0) {
    // For field columns, map to the expected format
    configuration.orderedDocumentTypeIds = args.fieldColumns.map((column) => ({
      documentTypeId: column.documentTypeId,
      required: column.required ?? true,
    }));
  } else if (args.documentTypes && args.documentTypes.length > 0) {
    // For document types, use as provided
    configuration.orderedDocumentTypeIds = args.documentTypes;
  } else {
    // Set empty array if no document types provided
    configuration.orderedDocumentTypeIds = [];
  }

  // Determine if this is a create or update operation
  const isUpdate =
    args.stackFilterId && args.stackFilterId !== pendingStackFilterId;
  const url = isUpdate
    ? `/search-filters/${args.stackFilterId}`
    : '/search-filters';
  const method = isUpdate ? 'PUT' : 'POST';

  const authTokenHttp = await getCompatibleAuthTokenHttp();
  const response = await authTokenHttp<SetStackFilterParams & { id?: string }>(
    url,
    {
      method,
      body: {
        type: 'stack',
        name: args.name,
        visibility,
        configuration,
      },
    },
  );

  if (response.data) return response.data;
  throw new Error('Failed to set stack filter');
};

export default setStackFilter;

const _mockStackFilter = (stackFilter: SetStackFilterParams) => {
  return {
    id: stackFilter.stackFilterId ?? crypto.randomUUID(),
    ...stackFilter,
  };
};
